import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import type { ScheduledWorkflow } from "@/reducers/openclawWorkflowsSlice"
import { formatSchedule } from "@/lib/formatWorkflowSchedule"
import {
  deleteCronAutomation,
  setCronAutomationStatus,
  updateCronAutomation,
} from "@/api/xeps/CronAutomations"
import { CRON_GONE, useAutomationMutation } from "@/hooks/useAutomationMutation"

export type CronAutomationDialogProps = {
  /** The job being edited; null closes the dialog. */
  job: ScheduledWorkflow | null
  onOpenChange: (open: boolean) => void
}

type Mode = "recurring" | "once"

const browserTz = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"

/** `2026-08-12T06:00` — what an `<input type="datetime-local">` expects. */
const toLocalInput = (iso?: string): string => {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** A 5-field expression is the only shape the agent's parser accepts. */
const isCronExpr = (expr: string): boolean =>
  expr.trim().split(/\s+/).length === 5

/**
 * Editing one of the user's own scheduled automations from the list.
 *
 * Only `cron` and `at` schedules get here — the caller filters. A geofence or
 * fuel rule shares the `event` kind but not the shape, and `cron_update` would
 * rewrite its schedule into a cron, which is a different rule entirely.
 */
export const CronAutomationDialog: React.FC<CronAutomationDialogProps> = ({
  job,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState("")
  const [message, setMessage] = useState("")
  const [mode, setMode] = useState<Mode>("recurring")
  const [expr, setExpr] = useState("")
  const [tz, setTz] = useState("")
  const [runAt, setRunAt] = useState("")
  const { saving, error, setError, run } = useAutomationMutation(CRON_GONE)

  // A handler job's behaviour lives in the agent, not in a prompt. Sending it
  // a message would overwrite the handler, so the field is not even offered.
  const isHandler = job?.payloadKind === "handler"

  useEffect(() => {
    if (!job) return
    const schedule = job.schedule
    setName(job.name)
    setMessage(job.message ?? "")
    setMode(schedule?.kind === "at" ? "once" : "recurring")
    setExpr(schedule?.expr ?? "")
    setTz(schedule?.tz || browserTz())
    setRunAt(toLocalInput(schedule?.ts))
    setError(null)
  }, [job, setError])

  const preview = useMemo(() => {
    if (mode === "once") {
      const d = new Date(runAt)
      return runAt && !Number.isNaN(d.getTime())
        ? formatSchedule({ kind: "at", ts: d.toISOString() }, "active", t)
        : ""
    }
    return isCronExpr(expr)
      ? formatSchedule({ kind: "cron", expr, tz }, "active", t)
      : ""
  }, [expr, mode, runAt, tz, t])

  const scheduleReady =
    mode === "once"
      ? Boolean(runAt) && !Number.isNaN(new Date(runAt).getTime())
      : isCronExpr(expr) && Boolean(tz.trim())

  const save = useCallback(async () => {
    if (!job || !name.trim() || !scheduleReady) return

    const ok = await run((client) =>
      updateCronAutomation(client, job.id, {
        name: name.trim(),
        // Omitted entirely for a handler job, and left alone when unchanged.
        message:
          isHandler || message === (job.message ?? "")
            ? undefined
            : message.trim(),
        ...(mode === "once"
          ? { runAt: new Date(runAt).toISOString() }
          : { schedule: expr.trim(), tz: tz.trim() }),
      }),
    )
    if (ok) onOpenChange(false)
  }, [
    expr,
    isHandler,
    job,
    message,
    mode,
    name,
    onOpenChange,
    run,
    runAt,
    scheduleReady,
    tz,
  ])

  const toggleStatus = useCallback(async () => {
    if (!job) return
    await run((client) =>
      setCronAutomationStatus(
        client,
        job.id,
        job.status === "active" ? "paused" : "active",
      ),
    )
  }, [job, run])

  const remove = useCallback(async () => {
    if (!job) return
    const ok = await run((client) => deleteCronAutomation(client, job.id))
    if (ok) onOpenChange(false)
  }, [job, onOpenChange, run])

  return (
    <Dialog open={job !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="truncate">{job?.name}</DialogTitle>
          <DialogDescription>
            {isHandler
              ? t("ai.cronDialog.descriptionHandler")
              : t("ai.cronDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cron-automation-name">
              {t("ai.cronDialog.name")}
            </Label>
            <Input
              id="cron-automation-name"
              value={name}
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {!isHandler && (
            <div className="space-y-1">
              <Label htmlFor="cron-automation-message">
                {t("ai.cronDialog.instruction")}
              </Label>
              <Textarea
                id="cron-automation-message"
                rows={3}
                value={message}
                disabled={saving}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label>{t("ai.cronDialog.when")}</Label>
            <div className="flex gap-2">
              {(["recurring", "once"] as Mode[]).map((option) => (
                <Button
                  key={option}
                  type="button"
                  variant={mode === option ? "default" : "outline"}
                  size="sm"
                  aria-pressed={mode === option}
                  disabled={saving}
                  onClick={() => setMode(option)}
                >
                  {option === "recurring"
                    ? t("ai.cronDialog.repeats")
                    : t("ai.cronDialog.once")}
                </Button>
              ))}
            </div>
          </div>

          {mode === "recurring" ? (
            <div className="flex gap-2">
              <div className="flex-1 space-y-1">
                <Label htmlFor="cron-automation-expr">
                  {t("ai.cronDialog.schedule")}
                </Label>
                <Input
                  id="cron-automation-expr"
                  value={expr}
                  disabled={saving}
                  placeholder="0 6 * * *"
                  onChange={(e) => setExpr(e.target.value)}
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label htmlFor="cron-automation-tz">
                  {t("ai.cronDialog.timezone")}
                </Label>
                <Input
                  id="cron-automation-tz"
                  value={tz}
                  disabled={saving}
                  placeholder="Europe/Warsaw"
                  onChange={(e) => setTz(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="cron-automation-run-at">
                {t("ai.cronDialog.runsAt")}
              </Label>
              <Input
                id="cron-automation-run-at"
                type="datetime-local"
                value={runAt}
                disabled={saving}
                onChange={(e) => setRunAt(e.target.value)}
              />
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {preview
              ? t("ai.cronDialog.runsPreview", { schedule: preview })
              : mode === "recurring"
                ? t("ai.cronDialog.cronHint")
                : t("ai.cronDialog.pickDateTime")}
          </p>

          {error && (
            <p className="text-xs text-red-500" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <div className="flex gap-1">
            <Button
              variant="ghost"
              className="text-red-500 hover:text-red-600"
              disabled={saving}
              onClick={remove}
            >
              {t("common.delete")}
            </Button>
            <Button variant="ghost" disabled={saving} onClick={toggleStatus}>
              {job?.status === "active"
                ? t("ai.automations.pause")
                : t("ai.automations.resume")}
            </Button>
          </div>
          <Button
            onClick={save}
            disabled={saving || !name.trim() || !scheduleReady}
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CronAutomationDialog
